import { useMemo } from 'react';
import { createFileRoute, Outlet, useChildMatches, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Shield } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules';
import { useWorkspaceHeader } from '@/components/layouts';
import { formatDate, roleType } from '@/components/organisms/settings-roles/role-display';
import { useRoleDetail, useRoles } from '@/hooks/settings/useRoles';
import { useHasPermission } from '@/hooks/usePermissions';
import {
  RESOURCE_LABELS,
  type RoleDetail,
  type RoleListItem,
  type RolePermission,
} from '@/types/settings';
import { usePurchasingRoleVisibility } from '@/features/purchasing/visibility';

export const Route = createFileRoute('/_dashboard/dashboard/settings/permissions/$id')({
  component: PermissionRoleDetailRoute,
});

function formatToken(value: string) {
  return value
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatScope(scope: RolePermission['scope']) {
  return scope === 'ALL' ? 'All' : formatToken(scope);
}

function PermissionRoleDetailRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const childMatches = useChildMatches();
  const { data: rolesData } = useRoles();
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData?.data]);

  const decodedId = decodeURIComponent(id);
  const listRole = useMemo(
    () => roles.find((candidate) => candidate.id === decodedId) ?? null,
    [decodedId, roles],
  );
  const { data: role, isLoading: roleLoading } = useRoleDetail(decodedId);

  const displayRole = role ?? listRole;
  const canEdit = useHasPermission('roles:edit');

  // Let the edit child route (/$id/edit) mount through the Outlet instead of
  // always rendering the read-only detail (mirrors users/$id).
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <RoleDetailContextHeaderSlot role={displayRole} />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <RoleDetailBody
          listRole={listRole}
          role={role ?? null}
          isLoadingRole={Boolean(decodedId) && roleLoading}
          canEdit={canEdit}
          onBack={() => void router.navigate({ to: '/dashboard/settings/permissions' })}
          onEdit={(roleId) =>
            void router.navigate({
              to: '/dashboard/settings/permissions/$id/edit',
              params: { id: roleId },
            })
          }
        />
      </div>
    </div>
  );
}

function RoleDetailBody({
  listRole,
  role,
  isLoadingRole,
  canEdit,
  onBack,
  onEdit,
}: {
  listRole: RoleListItem | null;
  role: RoleDetail | null;
  isLoadingRole: boolean;
  canEdit: boolean;
  onBack: () => void;
  onEdit: (roleId: string) => void;
}) {
  const displayRole = role ?? listRole;
  const permissionCount = listRole?.permissionCount ?? role?.permissions.length ?? 0;
  const membershipCount = listRole?.membershipCount ?? 0;
  const visibility = usePurchasingRoleVisibility();
  const permissionsByResource = useMemo(() => {
    const grouped = new Map<string, RolePermission[]>();
    for (const permission of role?.permissions ?? []) {
      // Purchasing and inventory groups only where their flag is on.
      if (!visibility.showResource(permission.resource)) continue;
      const resourcePermissions = grouped.get(permission.resource) ?? [];
      resourcePermissions.push(permission);
      grouped.set(permission.resource, resourcePermissions);
    }
    return Array.from(grouped.entries()).sort(([left], [right]) =>
      (RESOURCE_LABELS[left] ?? left).localeCompare(RESOURCE_LABELS[right] ?? right),
    );
  }, [role?.permissions, visibility]);
  const activeFlags = Object.entries(role?.capabilityFlags ?? {})
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);

  if (!displayRole) {
    return (
      <DetailCard label="Role" icon={Shield}>
        <p className="text-sm text-muted-foreground">
          {isLoadingRole ? 'Loading role...' : 'Role not found.'}
        </p>
      </DetailCard>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1 shrink-0 md:hidden"
              onClick={onBack}
              aria-label="Back to roles"
            >
              <ArrowLeft size={16} />
            </Button>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Shield className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-medium tracking-normal">{displayRole.name}</h1>
                <Badge
                  variant={displayRole.isSystem ? 'secondary' : 'outline'}
                  className="shrink-0"
                >
                  {roleType(displayRole)}
                </Badge>
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {displayRole.description || 'No description.'}
              </p>
            </div>
          </div>

          {canEdit ? (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => onEdit(displayRole.id)}
            >
              <Pencil className="size-4" />
              Edit
            </Button>
          ) : null}
        </div>

        <DetailFieldGrid className="mt-4 border-t pt-4 sm:grid-cols-2">
          <DetailField label="Permissions" value={permissionCount} />
          <DetailField label="Users" value={membershipCount} />
          <DetailField label="Status" value={displayRole.isActive ? 'Active' : 'Inactive'} />
          <DetailField label="Created" value={formatDate(displayRole.createdAt)} />
          <DetailField label="Updated" value={formatDate(displayRole.updatedAt)} />
        </DetailFieldGrid>
      </section>

      <DetailCard label={`Permissions (${permissionCount})`} icon={Shield}>
        {isLoadingRole && !role ? (
          <p className="text-sm text-muted-foreground">Loading permissions...</p>
        ) : permissionsByResource.length === 0 ? (
          <p className="text-sm text-muted-foreground">No permissions assigned.</p>
        ) : (
          <div className="space-y-2">
            {permissionsByResource.map(([resource, permissions]) => (
              <div key={resource} className="rounded-md border bg-muted/20 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">
                    {RESOURCE_LABELS[resource] ?? formatToken(resource)}
                  </p>
                  <span className="text-xs text-muted-foreground">{permissions.length} active</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {permissions.map((permission) => (
                    <Badge key={`${permission.resource}:${permission.action}`} variant="secondary">
                      {formatToken(permission.action)}: {formatScope(permission.scope)}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DetailCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <DetailCard label={`Special Permissions (${activeFlags.length})`} icon={Shield}>
          {isLoadingRole && !role ? (
            <p className="text-sm text-muted-foreground">Loading special permissions...</p>
          ) : activeFlags.length === 0 ? (
            <p className="text-sm text-muted-foreground">None enabled.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {activeFlags.map((flag) => (
                <Badge key={flag} variant="secondary">
                  {formatToken(flag)}
                </Badge>
              ))}
            </div>
          )}
        </DetailCard>

        <DetailCard label={`Hidden Fields (${role?.fieldMasks.length ?? 0})`} icon={Shield}>
          {isLoadingRole && !role ? (
            <p className="text-sm text-muted-foreground">Loading hidden fields...</p>
          ) : !role || role.fieldMasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">None configured.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {role.fieldMasks.map((mask) => (
                <Badge key={mask} variant="secondary">
                  {formatToken(mask)}
                </Badge>
              ))}
            </div>
          )}
        </DetailCard>
      </div>
    </div>
  );
}

function RoleDetailContextHeaderSlot({ role }: { role: RoleListItem | RoleDetail | null }) {
  const contextHeader = useMemo(() => {
    if (!role) return null;
    return (
      <div className="flex min-w-0 flex-col">
        <h2 className="truncate text-base font-semibold">{role.name}</h2>
        <p className="truncate text-xs text-muted-foreground">
          {role.description || 'View role access and assignments.'}
        </p>
      </div>
    );
  }, [role]);

  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}
